import React, { useEffect, useState } from "react";

export type FunnelFilter =
  | "all"
  | "booked"
  | "started"
  | "ongoing"
  | "completed"
  | "not_booked"
  | "not_started";

interface SlotCandidate {
  hasBooked: boolean;
  startTime: string | null;
  endTime: string | null;
  score: number | null;
  adminStatus?: number;
}

/**
 * The API reports Status = "Ongoing" for anyone with a startTime and no endTime, which never
 * expires — a candidate who starts and never submits stays "Ongoing" forever, long after their
 * window (and the slot) has ended. These two helpers derive liveness from the same clock the
 * "Time Remaining" column uses, so the funnel count and the row badge never disagree.
 * Display only: the raw API status still drives selection and the admin actions menu, so a
 * stuck candidate can still be reset / resent a link.
 */

/**
 * Candidate timestamps are UTC, but an API build that omits the trailing "Z" gets read by
 * `new Date()` as *local* time — shifting it by the client's offset, which made the countdown
 * hit 00:00 instantly. Only stamp "Z" when there is no offset, so an already-tagged string
 * (what the API sends now) is not corrupted into an invalid date.
 */
export const parseSlotTime = (value: string): Date => {
  const hasTimezone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasTimezone ? value : `${value}Z`);
};

/**
 * True once a started-but-unsubmitted candidate's assessment window has run out.
 * A missing/zero duration means "unknown" — never claim expiry in that case.
 */
export const isAssessmentWindowExpired = (
  candidate: { startTime: string | null; endTime: string | null },
  assessmentDuration: number,
  now: number = Date.now()
): boolean => {
  if (!candidate.startTime || candidate.endTime) return false;
  if (!assessmentDuration || assessmentDuration <= 0) return false;

  const startMs = parseSlotTime(candidate.startTime).getTime();
  if (Number.isNaN(startMs)) return false;

  return now - startMs >= assessmentDuration * 60 * 1000;
};

/**
 * Candidates sitting the test right now: started, not submitted, window still running.
 * Cancelled / paused candidates are excluded — they carry their own badge.
 */
export const isOngoingNow = (
  candidate: { startTime: string | null; endTime: string | null; adminStatus?: number },
  assessmentDuration: number,
  now: number = Date.now()
): boolean =>
  candidate.adminStatus !== -1 &&
  candidate.adminStatus !== 0 &&
  candidate.startTime !== null &&
  candidate.endTime === null &&
  !isAssessmentWindowExpired(candidate, assessmentDuration, now);

interface FunnelMetrics {
  total: number;
  booked: number;
  started: number;
  ongoing: number;
  completed: number;
  notBooked: number;
  notStarted: number;
}

export function computeMetrics(
  candidates: SlotCandidate[],
  assessmentDuration: number = 0,
  now: number = Date.now()
): FunnelMetrics {
  const total = candidates.length;
  const booked = candidates.filter((c) => c.hasBooked).length;
  const started = candidates.filter((c) => c.startTime !== null).length;
  const ongoing = candidates.filter((c) =>
    isOngoingNow(c, assessmentDuration, now)
  ).length;
  const completed = candidates.filter(
    (c) => c.endTime !== null && c.score !== null
  ).length;
  const notBooked = candidates.filter((c) => !c.hasBooked).length;
  const notStarted = candidates.filter(
    (c) => c.hasBooked && c.startTime === null
  ).length;

  return { total, booked, started, ongoing, completed, notBooked, notStarted };
}

interface CardConfig {
  key: FunnelFilter;
  label: string;
  getValue: (m: FunnelMetrics) => number;
  activeBg: string;
  activeText: string;
  activeBorder: string;
  dotColor: string;
}

const CARDS: CardConfig[] = [
  {
    key: "all",
    label: "Invited",
    getValue: (m) => m.total,
    activeBg: "bg-indigo-50 dark:bg-indigo-900/30",
    activeText: "text-indigo-700 dark:text-indigo-300",
    activeBorder: "border-indigo-300 dark:border-indigo-600",
    dotColor: "bg-indigo-500",
  },
  {
    key: "booked",
    label: "Booked",
    getValue: (m) => m.booked,
    activeBg: "bg-blue-50 dark:bg-blue-900/30",
    activeText: "text-blue-700 dark:text-blue-300",
    activeBorder: "border-blue-300 dark:border-blue-600",
    dotColor: "bg-blue-500",
  },
  {
    key: "started",
    label: "Started",
    getValue: (m) => m.started,
    activeBg: "bg-amber-50 dark:bg-amber-900/30",
    activeText: "text-amber-700 dark:text-amber-300",
    activeBorder: "border-amber-300 dark:border-amber-600",
    dotColor: "bg-amber-500",
  },
  {
    key: "ongoing",
    label: "Ongoing",
    getValue: (m) => m.ongoing,
    activeBg: "bg-yellow-50 dark:bg-yellow-900/30",
    activeText: "text-yellow-700 dark:text-yellow-300",
    activeBorder: "border-yellow-300 dark:border-yellow-600",
    dotColor: "bg-yellow-500",
  },
  {
    key: "completed",
    label: "Completed",
    getValue: (m) => m.completed,
    activeBg: "bg-emerald-50 dark:bg-emerald-900/30",
    activeText: "text-emerald-700 dark:text-emerald-300",
    activeBorder: "border-emerald-300 dark:border-emerald-600",
    dotColor: "bg-emerald-500",
  },
  {
    key: "not_booked",
    label: "Did Not Book",
    getValue: (m) => m.notBooked,
    activeBg: "bg-orange-50 dark:bg-orange-900/30",
    activeText: "text-orange-700 dark:text-orange-300",
    activeBorder: "border-orange-300 dark:border-orange-600",
    dotColor: "bg-orange-500",
  },
  {
    key: "not_started",
    label: "Did Not Start",
    getValue: (m) => m.notStarted,
    activeBg: "bg-rose-50 dark:bg-rose-900/30",
    activeText: "text-rose-700 dark:text-rose-300",
    activeBorder: "border-rose-300 dark:border-rose-600",
    dotColor: "bg-rose-500",
  },
];

interface CandidateFunnelBarProps {
  candidates: SlotCandidate[];
  activeFilter: FunnelFilter;
  onFilterChange: (filter: FunnelFilter) => void;
  /** Assessment length in minutes — used to age out candidates whose window has elapsed */
  assessmentDuration?: number;
}

const CandidateFunnelBar: React.FC<CandidateFunnelBarProps> = ({
  candidates,
  activeFilter,
  onFilterChange,
  assessmentDuration = 0,
}) => {
  // The Ongoing count is clock-dependent, so re-evaluate periodically instead of only
  // when the candidate list is refetched.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  const metrics = computeMetrics(candidates, assessmentDuration, now);

  return (
    <div className="mb-3 px-1">
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        {CARDS.map((card) => {
          const value = card.getValue(metrics);
          const isActive = activeFilter === card.key;

          return (
            <button
              key={card.key}
              onClick={() => onFilterChange(isActive ? "all" : card.key)}
              aria-pressed={isActive}
              title={`Filter table to show "${card.label}" candidates`}
              className={[
                "group flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium transition-all duration-200 select-none",
                isActive
                  ? `${card.activeBg} ${card.activeText} ${card.activeBorder} shadow-sm ring-1 ring-inset ${card.activeBorder.replace('border-', 'ring-')}`
                  : "bg-white dark:bg-darkbglight text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm",
              ].join(" ")}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${card.dotColor} ${isActive ? 'scale-110' : 'group-hover:scale-110 transition-transform'}`} />
                <span className={isActive ? 'font-semibold' : ''}>{card.label}</span>
              </div>
              
              <div className="flex items-center gap-1 ml-1 pl-3 border-l border-current opacity-60 group-hover:opacity-100 transition-opacity">
                <span className={`font-bold ${isActive ? 'text-base' : 'text-sm'}`}>{value}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export type { SlotCandidate as FunnelCandidate };
export default CandidateFunnelBar;
